import React, { useContext, useState } from "react";
import { Alert, Spin, Table, Tabs, Typography } from "antd";
import Axios from "axios";
import useRMateChart from "./useRMateChart";
import ChartPane from "./ChartPane";
import MapPropsContext from "../MapPropsContext";
import { getApiHost } from "../../../utils";
import {
  chartColor,
  processResponse,
  layoutForMultiSeriesStacked,
  layoutForCombination3DColumnLine,
} from "./RMateChartFusionCountryUtils";

import "./RMateChartFusionCountry.scss";
import useDebounce from "../../../utils/hooks/useDebounce";
import { StyledButton } from "../../../styled-components/Button";
import { TI_MODAL_RESULT_CLOSE_BUTTON } from "../../../Constants";

const { Title } = Typography;
const areaNameAry = ["공업지역", "관리지역", "녹지지역", "농림지역", "상업지역", "자연환경보전지역", "주거지역"];
const areaColAry = ["AREA_1", "AREA_2", "AREA_3", "AREA_4", "AREA_5", "AREA_6", "AREA_7"];

const RMateChartFusionCountry = ({ disabled }) => {
  const { chartAjaxParams } = useContext(MapPropsContext);

  const [prevParams, setPrevParams] = useState();
  const [isInit, setIsInit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [parsedObject, setParsedObject] = useState({});

  useDebounce(
    () => {
      if (isInit) {
        const url = getApiHost() + "/rwdinfolabstats/getRdwAnalsStatsData2.json";
        const isChanged = JSON.stringify(prevParams) !== JSON.stringify(chartAjaxParams);
        if (isChanged) {
          setPrevParams(chartAjaxParams);
          setIsLoading(true);
          setParsedObject({}); // 초기화

          Axios.get(url, { params: chartAjaxParams })
            .then(({ data }) => {
              const { gubun_code, prm_check, prm_check2 } = chartAjaxParams;
              setParsedObject(
                processResponse({
                  ...data,
                  areaNameAry,
                  areaColAry,
                  gubun_code,
                  prm_check,
                  prm_check2,
                })
              );
            })
            .catch((e) => console.error(e))
            .finally(() => {
              setIsLoading(false);
            });
        }
      }
    },
    1000,
    [isInit, setPrevParams, prevParams, chartAjaxParams, setIsLoading]
  );

  const { chart1Data, chart2Data, totalRdsData } = parsedObject;

  const [tabState, setTabState] = useState(0);
  return useRMateChart({
    init: () => {
      setIsInit(true);
    },
    style: { minHeight: "540px", padding: "10px" },
    disabled,
    children: (
      <div style={{ position: "relative", height: "100%", width: "100%", minHeight: "470px" }}>
        {isLoading && (
          <div
            style={{ position: "absolute", top: 55, zIndex: 1, height: "90%", width: "100%", backgroundColor: "white" }}
          >
            <Spin size={"large"}>
              <Alert
                message="차트 로딩 중"
                description="선택한 무선국의 정보를 불러오는 중입니다.."
                type="info"
                showIcon
                style={{ height: "470px" }}
              />
            </Spin>
          </div>
        )}
        <div style={{ paddingBottom: "5px" }}>
          <StyledButton
            isActive={tabState === 0}
            onClick={() => setTabState(0)}
            style={{ borderRadius: "4px 0px 0px 4px", padding: "0px 4px" }}
            tabIndex={TI_MODAL_RESULT_CLOSE_BUTTON - 3}
          >
            무선국 밀도
          </StyledButton>
          <StyledButton
            isActive={tabState === 1}
            onClick={() => setTabState(1)}
            style={{ borderRadius: "0px 0px 0px 0px", padding: "0px 4px" }}
            tabIndex={TI_MODAL_RESULT_CLOSE_BUTTON - 2}
          >
            용도지역별 무선국 수 / 면적(㎢)
          </StyledButton>
          <StyledButton
            isActive={tabState === 2}
            onClick={() => setTabState(2)}
            style={{ borderRadius: "0px 4px 4px 0px", padding: "0px 4px" }}
            tabIndex={TI_MODAL_RESULT_CLOSE_BUTTON - 1}
          >
            지역별 무선국 밀도 (국수/㎢)
          </StyledButton>
        </div>
        {!isLoading && !chart1Data && <Alert type={"error"} message={"조회된 차트 데이터가 없습니다."} />}
        {tabState === 0 && chart1Data && (
          <>
            <Title
              level={4}
              style={{
                fontSize: "19px",
                color: "#4a4f66",
                textAlign: "center",
                padding: 4,
              }}
            >
              무선국 밀도(국수/㎢)
            </Title>
            <Table
              columns={[
                {
                  dataIndex: "AR_NAME",
                  key: "COLOR",
                  width: 40,
                  render: (AR_NAME) => <div style={{ width: 20, height: 20, backgroundColor: chartColor(AR_NAME) }} />,
                },
                { title: "지역", dataIndex: "AR_NAME", key: "AR_NAME" },
                {
                  title: "밀도",
                  dataIndex: "RDS_AREA",
                  key: "RDS_AREA",
                  render: (RDS_AREA) => <div style={{ textAlign: "right" }}>{RDS_AREA}</div>,
                },
              ]}
              dataSource={[
                { AR_NAME: "전체", RDS_AREA: totalRdsData },
                ...chart1Data.filter(({ RDS_AREA }) => !!RDS_AREA),
              ]}
              rowKey={"AR_NAME"}
              pagination={false}
            />
          </>
        )}

        {tabState === 1 && (
          <ChartPane
            data={chart1Data}
            processor={() => chart1Data}
            layoutProcessor={() => {
              const { searchYear } = chartAjaxParams;
              return layoutForCombination3DColumnLine({
                chartBaseData: CHART_BASE_DATA1,
                areaNameAry,
                searchYear: searchYear,
              });
            }}
            style={{ height: 470 }}
          />
        )}

        {tabState === 2 && (
          <ChartPane
            data={chart2Data}
            processor={() => chart2Data}
            layoutProcessor={() => {
              const { gubun_code, searchYear } = chartAjaxParams;
              return layoutForMultiSeriesStacked({
                chartBaseData: CHART_BASE_DATA2,
                areaNameAry,
                areaColAry,
                gubun_code,
                searchYear: searchYear,
              });
            }}
            style={{ height: 470 }}
          />
        )}
        {/* <Tabs defaultActiveKey={"3"} type={"card"}>
          <Tabs.TabPane tab="무선국 밀도" key={"3"}>
            {!isLoading && !chart1Data && <Alert type={"error"} message={"조회된 차트 데이터가 없습니다."} />}
            {chart1Data && (
              <>
                <Title
                  level={4}
                  style={{
                    fontSize: "19px",
                    color: "#4a4f66",
                    textAlign: "center",
                    padding: 4,
                  }}
                >
                  무선국 밀도(국수/㎢)
                </Title>
                <Table
                  columns={[
                    {
                      dataIndex: "AR_NAME",
                      key: "COLOR",
                      width: 40,
                      render: (AR_NAME) => (
                        <div style={{ width: 20, height: 20, backgroundColor: chartColor(AR_NAME) }} />
                      ),
                    },
                    { title: "지역", dataIndex: "AR_NAME", key: "AR_NAME" },
                    {
                      title: "밀도",
                      dataIndex: "RDS_AREA",
                      key: "RDS_AREA",
                      render: (RDS_AREA) => <div style={{ textAlign: "right" }}>{RDS_AREA}</div>,
                    },
                  ]}
                  dataSource={[
                    { AR_NAME: "전체", RDS_AREA: totalRdsData },
                    ...chart1Data.filter(({ RDS_AREA }) => !!RDS_AREA),
                  ]}
                  rowKey={"AR_NAME"}
                  pagination={false}
                />
              </>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab="용도지역별 무선국 수 / 면적(㎢)" key={"1"}>
            <ChartPane
              data={chart1Data}
              processor={() => chart1Data}
              layoutProcessor={() => {
                const { searchYear } = chartAjaxParams;
                return layoutForCombination3DColumnLine({
                  chartBaseData: CHART_BASE_DATA1,
                  areaNameAry,
                  searchYear: searchYear,
                });
              }}
              style={{ height: 470 }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="지역별 무선국 밀도 (국수/㎢)" key={"2"}>
            <ChartPane
              data={chart2Data}
              processor={() => chart2Data}
              layoutProcessor={() => {
                const { gubun_code, searchYear } = chartAjaxParams;
                return layoutForMultiSeriesStacked({
                  chartBaseData: CHART_BASE_DATA2,
                  areaNameAry,
                  areaColAry,
                  gubun_code,
                  searchYear: searchYear,
                });
              }}
              style={{ height: 470 }}
            />
          </Tabs.TabPane>
        </Tabs> */}
      </div>
    ),
  });
};

const CHART_BASE_DATA1 = {
  xtitle: "용도지역",
  ytitle: "면적(㎢)",
  ytitle2: "무선국 수",
  layOutStyle: "Master",
  masterName: "무선국 밀도",
  valueFixed: 5,
};

const CHART_BASE_DATA2 = {
  xtitle: "지역명",
  ytitle: "무선국 밀도\r\n(국수/㎢)",
  layOutStyle: "Detail",
  masterName: "무선국 밀도",
  valueFixed: 5,
};

export default RMateChartFusionCountry;
